import { h, Component } from 'preact';
import { createPortal } from 'preact/compat';
import { disableBodyScroll, enableBodyScroll } from '../../lib/Utils';
import './index.less';
import {
  setNativeModalClosed,
  setNativeModalOpened
} from '../../../../../nativeConnectors/platform';

const ANIMATION_TIME = 500;

/**
 * Gives option to close modal on back button click
 * closeModalOnBackButton = true [default]
 */

class Modal extends Component {
  state = {
    shouldDisplay: false,
    className: ''
  };

  modalUpdates = null;
  ANIMATION_TIME = ANIMATION_TIME;

  componentDidMount () {
    const { animationTime } = this.props;
    if (typeof animationTime === 'number') {
      this.ANIMATION_TIME = animationTime;
    }
    if (this.props.isVisible) {
      this.showModal();
      disableBodyScroll();
    }
  }

  componentWillUnmount () {
    setNativeModalClosed();
    enableBodyScroll();
    this.handleCloseModalFromOutside();
  }

  componentDidUpdate (previousProps, previousState) {
    if (!previousProps.isVisible && this.props.isVisible) {
      this.showModal();
    } else if (!previousState.shouldDisplay && this.state.shouldDisplay) {
      window.requestAnimationFrame(() => {
        this.setClassName('fade-in');
      });
    } else if (previousProps.isVisible && !this.props.isVisible) {
      this.handleCloseModalFromOutside();
      this.setClassName();
    } else if (previousState.className && !this.state.className) {
      setTimeout(() => {
        this.setState({ shouldDisplay: false }, () => {
          if (this.props.onModalHide) {
            this.props.onModalHide();
          }
        });
      }, this.ANIMATION_TIME);
    }

    if (previousState.shouldDisplay !== this.state.shouldDisplay) {
      if (this.state.shouldDisplay) {
        // IMPORTANT: Picked from CSS file
        setNativeModalOpened({ color: 'rgba(0,0,0,0.5)' });
      } else {
        setNativeModalClosed();
      }
    }
  }

  goToPrevRoute = () => {
    this.modalUpdates.historyUpdate = true;
    if (history.length === 1) {
      // TODO: can be used later when opening shared modal state through url
      return history.replaceState(null, '', window.location);
    }
    history.go(-1);
  };

  isBackButtonCloseEnabled = () => {
    const { closeModalOnBackButton = true } = this.props;
    return closeModalOnBackButton;
  };

  manageHistoryAddedOnModalOpen = () => {
    if (!this.isBackButtonCloseEnabled()) {
      return;
    }
    const { historyUpdate } = this.modalUpdates;
    if (!historyUpdate) {
      this.goToPrevRoute();
    }
  };

  handleCloseModalFromOutside = () => {
    const backButtonCloseEnabled = this.isBackButtonCloseEnabled();
    if (!backButtonCloseEnabled || this.modalUpdates === null) {
      return;
    }
    this.modalUpdates.visibility = true;
    this.removeBackButtonClickListener();
    this.manageHistoryAddedOnModalOpen();
  };

  showModal = () => {
    this.setState({ shouldDisplay: true });
    this.addBackButtonClickListener();
  };

  setClassName = (className = '') => this.setState({ className });

  backClickListener = (event) => {
    event.preventDefault();
    event.srcElement.focus();
    this.modalUpdates.historyUpdate = true;
    this.props.onBackdropClick();
  };

  addBackButtonClickListener = () => {
    const { onBackdropClick } = this.props;
    const backButtonCloseEnabled = this.isBackButtonCloseEnabled();

    if (!backButtonCloseEnabled || typeof onBackdropClick !== 'function') {
      return;
    }

    this.modalUpdates = { historyUpdate: false, visibility: false, clearListener: false };
    history.pushState(null, '', window.location);
    window.addEventListener('popstate', this.backClickListener);
  };

  removeBackButtonClickListener = () => {
    window.removeEventListener('popstate', this.backClickListener);
    this.modalUpdates.clearListener = true;
  };

  onModalContainerClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    const { onBackdropClick } = this.props;

    if (onBackdropClick) {
      if (this.isBackButtonCloseEnabled()) {
        return this.goToPrevRoute();
      }
      onBackdropClick();
    }
  };

  onChildrenContainerClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    return false;
  };

  render () {
    const { shouldDisplay, className } = this.state;

    if (!shouldDisplay) {
      return null;
    }

    const { className: modalClassName, containerClassName = '', style = {}, children } = this.props;

    return createPortal(
      <div
        className={`qs-modal modal-container ${containerClassName} ${className}`}
        onClick={this.onModalContainerClick}
      >
        <div className={modalClassName} style={style} onClick={this.onChildrenContainerClick}>
          {children}
        </div>
      </div>,
      document.body
    );
  }
}

export default Modal;

export { ANIMATION_TIME };
